import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faEnvelope, faEye, faEyeSlash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { completePasswordReset, validatePassword } from '../../services/supplierApi';
import wedlLogo from '../../assets/images/wedl_logo.svg';
import wedlHintergrund from '../../assets/images/wedl_hintergrund.svg';

const PasswordResetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [resetToken] = useState(searchParams.get('token') || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<{
    isValid: boolean;
    errors: string[];
    strength: 'weak' | 'medium' | 'strong';
  } | null>(null);

  useEffect(() => {
    if (!resetToken) {
      setError('Ungültiger Reset-Link. Bitte fordern Sie einen neuen an.');
    }
  }, [resetToken]);

  const handlePasswordValidation = async (password: string) => {
    if (password.length === 0) {
      setPasswordValidation(null);
      return;
    }
    
    try {
      const result = await validatePassword(password);
      setPasswordValidation(result.validation);
    } catch (error) {
      console.error('Password validation error:', error);
      // Fallback validation
      const errors: string[] = [];
      
      if (password.length < 12) {
        errors.push('Das Passwort muss mindestens 12 Zeichen lang sein');
      }
      if (!/[A-Z]/.test(password)) {
        errors.push('Das Passwort muss mindestens einen Großbuchstaben enthalten');
      }
      const numberCount = (password.match(/\d/g) || []).length;
      if (numberCount < 3) {
        errors.push('Das Passwort muss mindestens 3 Zahlen enthalten');
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors.push('Das Passwort muss mindestens ein Sonderzeichen enthalten');
      }
      
      const fallbackValidation = {
        isValid: errors.length === 0,
        errors,
        strength: (errors.length === 0 ? (password.length >= 16 ? 'strong' : 'medium') : 'weak') as 'weak' | 'medium' | 'strong'
      };
      
      setPasswordValidation(fallbackValidation);
    }
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);
    handlePasswordValidation(password);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!resetToken) {
        throw new Error('Ungültiger Reset-Token');
      }

      if (!newPassword || !confirmPassword) {
        throw new Error('Alle Felder sind erforderlich');
      }

      if (newPassword !== confirmPassword) {
        throw new Error('Die Passwörter stimmen nicht überein');
      }

      if (!passwordValidation?.isValid) {
        throw new Error('Das Passwort erfüllt nicht die Sicherheitsanforderungen');
      }

      await completePasswordReset(resetToken, newPassword);
      setIsSuccess(true);

    } catch (error: any) {
      setError(error.message || 'Fehler beim Zurücksetzen des Passworts');
    } finally {
      setIsLoading(false);
    }
  };

  const getStrengthColor = (strength: 'weak' | 'medium' | 'strong') => {
    switch (strength) {
      case 'weak': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'strong': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getStrengthText = (strength: 'weak' | 'medium' | 'strong') => {
    switch (strength) {
      case 'weak': return 'Schwach';
      case 'medium': return 'Mittel';
      case 'strong': return 'Stark';
      default: return '';
    }
  };

  if (isSuccess) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-8 bg-gray-100"
        style={{
          background: `linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), url(${wedlHintergrund})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md mx-auto"
        >
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl p-8 border border-gray-200">
            <div className="text-center">
              <img 
                src={wedlLogo} 
                alt="WEDL Logo" 
                className="mx-auto mb-6 h-12 w-auto"
              />
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Passwort erfolgreich zurückgesetzt!
              </h2>
              <p className="text-gray-600 mb-6">
                Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  <strong>Wichtig:</strong> Bei der nächsten Anmeldung müssen Sie Ihr Passwort möglicherweise erneut ändern.
                </p>
              </div>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 font-medium shadow-lg transition-all duration-200"
              >
                Zur Anmeldung
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!resetToken) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-8 bg-gray-100"
        style={{
          background: `linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), url(${wedlHintergrund})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md mx-auto"
        >
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl p-8 border border-gray-200">
            <div className="text-center">
              <img 
                src={wedlLogo} 
                alt="WEDL Logo" 
                className="mx-auto mb-6 h-12 w-auto"
              />
              <div className="text-4xl mb-4">❌</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ungültiger Reset-Link</h3>
              <p className="text-gray-600 mb-6">
                Dieser Reset-Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an.
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 font-medium shadow-lg transition-all duration-200"
              >
                Zur Anmeldung
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-8 bg-gray-100"
      style={{
        background: `linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), url(${wedlHintergrund})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mx-auto"
      >
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl p-8 border border-gray-200">
          {/* Header */}
          <div className="text-center mb-8">
            <img 
              src={wedlLogo} 
              alt="WEDL Logo" 
              className="mx-auto mb-6 h-12 w-auto"
            />
            <div className="flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faKey} className="text-red-600 text-2xl mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">
                Neues Passwort erstellen
              </h2>
            </div>
            <p className="text-gray-600">
              Geben Sie Ihr neues Passwort ein
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FontAwesomeIcon icon={faKey} className="mr-2" />
                Neues Passwort
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {passwordValidation && (
                <div className="mt-2">
                  <div className={`text-sm ${getStrengthColor(passwordValidation.strength)}`}>
                    Stärke: {getStrengthText(passwordValidation.strength)}
                  </div>
                  {!passwordValidation.isValid && (
                    <ul className="text-sm text-red-600 mt-1">
                      {passwordValidation.errors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FontAwesomeIcon icon={faKey} className="mr-2" />
                Passwort bestätigen
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              
              {/* Confirmation Check */}
              {confirmPassword && newPassword && confirmPassword !== newPassword && (
                <p className="text-sm text-red-600 mt-1">
                  Die Passwörter stimmen nicht überein
                </p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !passwordValidation?.isValid || newPassword !== confirmPassword || !newPassword || !confirmPassword}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium shadow-lg transition-all duration-200"
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  Wird zurückgesetzt...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faKey} className="mr-2" />
                  Passwort zurücksetzen
                </>
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Passwort-Anforderungen:</h3>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Mindestens 12 Zeichen lang</li>
              <li>• Mindestens 1 Großbuchstabe</li>
              <li>• Mindestens 3 Zahlen</li>
              <li>• Mindestens 1 Sonderzeichen (!@#$%^&*)</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PasswordResetPage;